import React, { useEffect, useState, useCallback } from 'react';
import { firestore } from '../firebase';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const CARD  = '#111';
const BDR   = 'rgba(255,255,255,0.07)';
const WHITE = '#EAEAEA';
const MUTED = '#555';
const PINK  = '#FF1F6A';
const ACC   = '#C8FF00';

const fmt = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('fr-CA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const deviceIcon  = (d) => ({ Mobile: '📱', Tablette: '📲', Desktop: '🖥' }[d] || '🖥');
const browserIcon = (b) => ({ Chrome: '🟡', Safari: '🔵', Firefox: '🟠', Edge: '🔷' }[b] || '🌐');

function StatCard({ label, value, color, sub }) {
  return (
    <div style={{ background: CARD, borderRadius: 12, padding: '18px 22px', border: `1px solid ${BDR}`, flex: 1, minWidth: 120 }}>
      <div style={{ fontSize: '2rem', fontWeight: 900, color: color || ACC, letterSpacing: '-0.04em', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      <div style={{ fontSize: '0.76rem', color: MUTED, marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: '0.7rem', color: '#444', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function VisitorMap({ visitors }) {
  const mapped = visitors.filter(v => v.lat && v.lng);

  const countByPos = {};
  mapped.forEach(v => {
    const key = `${v.lat.toFixed(2)},${v.lng.toFixed(2)}`;
    if (!countByPos[key]) countByPos[key] = { lat: v.lat, lng: v.lng, count: 0, city: v.city, country: v.country, ips: new Set() };
    countByPos[key].count++;
    countByPos[key].ips.add(v.ip);
  });

  const pins = Object.values(countByPos);

  return (
    <div style={{ borderRadius: 14, overflow: 'hidden', border: `1px solid ${BDR}`, position: 'relative' }}>
      <style>{`
        .leaflet-container { background: #0d0d14 !important; }
        .leaflet-tile { filter: brightness(0.55) saturate(0.6) hue-rotate(200deg); }
        .leaflet-control-attribution { display: none !important; }
        .leaflet-tooltip { background: #111 !important; border: 1px solid rgba(255,31,106,0.4) !important; color: #eaeaea !important; font-size: 0.78rem; font-family: Inter, system-ui, sans-serif; box-shadow: 0 4px 20px rgba(0,0,0,0.6) !important; border-radius: 8px !important; padding: 6px 10px !important; }
        .leaflet-tooltip::before { display: none; }
        .leaflet-zoom-animated { background: transparent !important; }
      `}</style>
      <MapContainer
        center={[30, 0]}
        zoom={2}
        minZoom={1}
        maxZoom={12}
        style={{ height: 420, width: '100%' }}
        zoomControl={true}
        scrollWheelZoom={true}
        worldCopyJump={true}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution=""
          noWrap={false}
        />
        {pins.map((p, i) => (
          <CircleMarker
            key={i}
            center={[p.lat, p.lng]}
            radius={Math.min(6 + p.count * 2, 18)}
            pathOptions={{ color: PINK, fillColor: PINK, fillOpacity: 0.85, weight: 1.5 }}
          >
            <Tooltip direction="top" offset={[0, -6]} opacity={1}>
              <div>
                <div style={{ fontWeight: 700, color: PINK, marginBottom: 2 }}>{p.city || 'Ville inconnue'}{p.country ? `, ${p.country}` : ''}</div>
                <div>{p.count} visite{p.count > 1 ? 's' : ''} · {p.ips.size} IP{p.ips.size > 1 ? 's' : ''}</div>
              </div>
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>
      {mapped.length === 0 && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div style={{ textAlign: 'center', color: MUTED }}>
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>🌍</div>
            <div style={{ fontSize: '0.85rem' }}>Aucune donnée de géolocalisation<br />Les prochaines visites seront localisées automatiquement.</div>
          </div>
        </div>
      )}
      <div style={{ position: 'absolute', bottom: 10, right: 10, zIndex: 9000, background: 'rgba(0,0,0,0.7)', borderRadius: 8, padding: '4px 10px', fontSize: '0.72rem', color: MUTED, border: `1px solid ${BDR}` }}>
        {mapped.length} visiteur{mapped.length > 1 ? 's' : ''} localisé{mapped.length > 1 ? 's' : ''}
      </div>
    </div>
  );
}

export default function VibeeStatsSection() {
  const [visitors, setVisitors] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState('all');
  const [view,     setView]     = useState('map');
  const [enriching, setEnriching] = useState(false);
  const [enrichMsg, setEnrichMsg] = useState('');

  useEffect(() => {
    const q = query(collection(firestore, 'vibeeVisitors'), orderBy('at', 'desc'), limit(500));
    const unsub = onSnapshot(q, (snap) => {
      setVisitors(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  const enrichGeo = useCallback(async () => {
    setEnriching(true);
    setEnrichMsg('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const r = await fetch(`${apiBase}/api/vibee-stats/geo-enrich`, { method: 'POST' });
      const d = await r.json();
      setEnrichMsg(d.enriched > 0 ? `✓ ${d.enriched} visite${d.enriched > 1 ? 's' : ''} localisée${d.enriched > 1 ? 's' : ''}` : 'Déjà à jour');
    } catch { setEnrichMsg('Erreur'); }
    setEnriching(false);
  }, []);

  const now   = new Date();
  const today = visitors.filter(v => v.at?.toDate && (now - v.at.toDate()) < 86400000);
  const week  = visitors.filter(v => v.at?.toDate && (now - v.at.toDate()) < 604800000);

  const uniqueIPs   = new Set(visitors.map(v => v.ip)).size;
  const uniqueToday = new Set(today.map(v => v.ip)).size;

  const dc = { Mobile: 0, Tablette: 0, Desktop: 0 };
  visitors.forEach(v => { if (dc[v.device] !== undefined) dc[v.device]++; });

  const filtered = filter === 'today' ? today : filter === 'week' ? week : visitors;

  return (
    <div style={{ padding: '28px 32px', fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h2 style={{ fontSize: '1.3rem', fontWeight: 900, color: WHITE, margin: 0 }}>
          <span style={{ color: PINK }}>Vy</span>bme — Visiteurs
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22C55E', boxShadow: '0 0 6px #22C55E' }} />
            <span style={{ fontSize: '0.75rem', color: '#22C55E', fontWeight: 600 }}>Temps réel</span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 28 }}>
        <StatCard label="Visites totales"  value={visitors.length} color={WHITE} />
        <StatCard label="IPs uniques"      value={uniqueIPs}       color={ACC} />
        <StatCard label="Aujourd'hui"      value={today.length}    color={PINK} sub={`${uniqueToday} IPs uniques`} />
        <StatCard label="Cette semaine"    value={week.length}     color='#A78BFA' />
        <StatCard label="📱 Mobile"        value={dc.Mobile}       color='#38BDF8' />
        <StatCard label="🖥 Desktop"       value={dc.Desktop}      color='#34D399' />
      </div>

      {/* Onglets Vue */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center' }}>
        {[['map', '🗺 Carte'], ['list', '📋 Liste']].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)}
            style={{ padding: '7px 18px', borderRadius: 10, background: view === k ? PINK : 'rgba(255,255,255,0.05)', border: `1px solid ${view === k ? PINK : BDR}`, color: WHITE, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
            {l}
          </button>
        ))}
        {view === 'map' && (
          <button onClick={enrichGeo} disabled={enriching}
            style={{ padding: '7px 14px', borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: `1px solid ${BDR}`, color: enriching ? MUTED : WHITE, fontWeight: 600, fontSize: '0.78rem', cursor: enriching ? 'default' : 'pointer', fontFamily: 'inherit' }}>
            {enriching ? '…' : '🔄 Localiser les visites existantes'}
          </button>
        )}
        {enrichMsg && <span style={{ fontSize: '0.78rem', color: '#22C55E', fontWeight: 600 }}>{enrichMsg}</span>}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px', color: MUTED }}>Chargement…</div>
      ) : view === 'map' ? (
        <VisitorMap visitors={visitors} />
      ) : (
        <>
          {/* Filtres liste */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
            {[['all', 'Tout'], ['today', "Aujourd'hui"], ['week', 'Cette semaine']].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)}
                style={{ padding: '6px 14px', borderRadius: 8, background: filter === k ? PINK : 'rgba(255,255,255,0.05)', border: `1px solid ${filter === k ? PINK : BDR}`, color: WHITE, fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
                {l}
              </button>
            ))}
            <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: MUTED }}>
              {filtered.length} entrée{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>

          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px', color: MUTED }}>Aucun visiteur enregistré.</div>
          ) : (
            <div style={{ background: CARD, borderRadius: 12, border: `1px solid ${BDR}`, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${BDR}` }}>
                    {['Date', 'IP', 'Lieu', 'Appareil', 'Navigateur', 'Page'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 200).map((v, i) => (
                    <tr key={v.id || i}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      style={{ borderBottom: `1px solid rgba(255,255,255,0.04)`, cursor: 'default' }}>
                      <td style={{ padding: '11px 16px', fontSize: '0.8rem', color: MUTED, whiteSpace: 'nowrap' }}>{fmt(v.at)}</td>
                      <td style={{ padding: '11px 16px', fontSize: '0.82rem', color: WHITE, fontFamily: 'monospace', fontWeight: 600 }}>{v.ip || '—'}</td>
                      <td style={{ padding: '11px 16px', fontSize: '0.78rem', color: MUTED, whiteSpace: 'nowrap' }}>
                        {v.city ? `${v.city}${v.country ? `, ${v.country}` : ''}` : '—'}
                      </td>
                      <td style={{ padding: '11px 16px', fontSize: '0.82rem', color: WHITE }}>{deviceIcon(v.device)} {v.device}</td>
                      <td style={{ padding: '11px 16px', fontSize: '0.82rem', color: WHITE }}>{browserIcon(v.browser)} {v.browser}</td>
                      <td style={{ padding: '11px 16px', fontSize: '0.78rem', color: MUTED }}>{v.page || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
